import { notifications } from '@mantine/notifications';
import { useMutation, useQuery } from '@tanstack/react-query';
import { MRT_ColumnFiltersState } from 'mantine-react-table';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResultsClause } from '../components/results-help/results-clause-help-content';
import { resultsApi } from '../data-access/apis/results';

import { FORM_STATUS, MultisectActionDto, useSharedQueries } from '@data-access';

type UseResultsExportParams = {
  clause: ResultsClause;
  filters: MRT_ColumnFiltersState;
};

export function useResultsExport({ clause, filters }: UseResultsExportParams) {
  const { t } = useTranslation();
  const sharedQueries = useSharedQueries();
  const exportDisabledForClause = clause === 'gp';
  const [computationRequestId, setComputationRequestId] = useState<
    number | null
  >(null);
  const hasDownloadedRef = useRef(false);

  const exportSynthesis = useMutation({
    mutationFn: resultsApi.exportSynthesis,
    onSuccess: (data) => {
      hasDownloadedRef.current = false;
      setComputationRequestId(data.instance_id);
    },
  });

  const downloadExport = useMutation({
    mutationFn: resultsApi.downloadExport,
  });

  const computationRequest = useQuery({
    ...sharedQueries.computationRequest({ id: computationRequestId! }),
    enabled: computationRequestId !== null,
  });

  useEffect(() => {
    if (
      computationRequest.data?.status === FORM_STATUS.FINISHED &&
      !hasDownloadedRef.current &&
      !downloadExport.isPending
    ) {
      hasDownloadedRef.current = true;
      downloadExport.mutate({ id: computationRequest.data.id });
      setComputationRequestId(null);
    } else if (computationRequest.data?.status === FORM_STATUS.ERROR) {
      notifications.show({
        title: t('common.ERROR'),
        message: t('common.error.pageError'),
        color: 'red',
      });
      setComputationRequestId(null);
    }
  }, [computationRequest.data, downloadExport, t]);

  const handleExport = () => {
    if (exportDisabledForClause) {
      return;
    }

    const payload: MultisectActionDto = {
      ids: [],
      all: true,
      selectedFilters: filters,
    };
    exportSynthesis.mutate(payload);
  };

  const isExportPending =
    exportSynthesis.isPending ||
    downloadExport.isPending ||
    computationRequest.data?.status === FORM_STATUS.PENDING;

  const isExportLoading = !exportDisabledForClause && isExportPending;

  const isExportDisabled = exportDisabledForClause || isExportPending;

  return {
    handleExport,
    isExportLoading,
    isExportDisabled,
    exportDisabledForClause,
  };
}
